
import React, { useState, Fragment, useMemo } from 'react'
import Swal from 'sweetalert2'
import { useHistory } from 'react-router-dom'

import { useTeams } from '@/Hooks/UseTeam'
import useTranslation from '@/Hooks/UseTranslation'
import { useAuth } from '@/Hooks/UseAuth'
import { useProject } from '@/Hooks/UseProject'

import UserAvatar from '@/Components/UserAvatar/UserAvatar'
import OptionsDialog from '@/Components/OptionsDialog/OptionsDialog'
import TeamSelector from '@/Components/TeamSelector/TeamSelector'

const ActiveUsers = () => {

  const { t } = useTranslation()

  const history = useHistory()

  const { ability } = useAuth()

  const { project } = useProject()

  const {
    teams,
    teamSelected,
    moveUserToTeam,
    removeUserFromTeam,
    setUserShowTasksMode,
    toggleAssistant,
  } = useTeams()

  const [user, setUser] = useState<any>(undefined)
  const [showUserOptions, setShowUserOptions] = useState(false)

  const [ showTeamSelector, setShowTeamSelector ] = useState(false)

  const options = useMemo(() => {

    const options: any[] = []

    if (!user) return options

    if (ability.can('set-assistant', 'team-member')) {

      const isPl4nner = project.user === user._id

      if (!isPl4nner) {

        const label = user.isAssistant
          ? t('TEAMS:ContextMenuRemoveAssistantOption')
          : t('TEAMS:ContextMenuAddAssistantOption')

        options.push({
          icon: <i className="fas fa-fw fa-hard-hat"></i>,
          label: label,
          onClick: () => toggleAssistant(user._id, !user.isAssistant ? 'add' : 'remove')
        })
      }
    }

    if (ability.can('move', 'TeamMember')) {

      options.push({
        icon: <span className="icon-cambiar_equipo" />,
        label: t('TEAMS:ContextMenuSwapTeamOption'),
        onClick: () => setShowTeamSelector(true)
      })
    }

    if (user.profile_tag !== 'pl4nner' && user.profile_tag !== 'observer') {

      options.push({
        icon: <span className="icon-tareas_usuario" />,
        label: t('TEAMS:ContextMenuShowOnlyMyTasksOption'),
        color: user.showOnlyMyTasks ? 'var(--color-green)' : 'var(--color-secondary)',
        onClick: () => toggleUserShowTasksMode(user._id, user)
      })
    }

    if (ability.can('remove', 'TeamMember')) {

      options.push({
        icon: <span className="icon-eliminar" />,
        color: 'var(--color-danger)',
        label: t('TEAMS:ContextMenuRemoveTeamMemberOption'),
        onClick: () => removeUser(user._id)
      })
    }

    return options
  }, [ user, showUserOptions ])

  const removeUser = async (user_id: string) => {
    const result = await Swal.fire({
      title: t('TEAMS:RemoveUserAlertTitle'),
      text: t('TEAMS:RemoveUserAlertContent'),
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: t('TEAMS:RemoveUserAlertCancelButton'),
      confirmButtonText: t('TEAMS:RemoveUserAlertConfirmButton'),
      width: "500px"
    })

    if (result.isConfirmed) {

      await removeUserFromTeam(user_id, teamSelected._id)
    }
  }

  const toggleUserShowTasksMode = async (user_id: string, user: any) => {
    if (!user.showOnlyMyTasks) {
      const result = await Swal.fire({
        title: t('TEAMS:ShowOnlyMyTasksAlertTitle'),
        text: t('TEAMS:ShowOnlyMyTasksAlertContent'),
        icon: "question",
        showCancelButton: true,
        cancelButtonText: t('TEAMS:ShowOnlyMyTasksAlertCancelButton'),
        confirmButtonText: t('TEAMS:ShowOnlyMyTasksAlertConfirmButton'),
        width: "500px"
      })

      if (result.isConfirmed) {

        // dispatch(TeamsActions.toggleShowOnlyMyTasks(project._id, user_id, true))
        await setUserShowTasksMode(user_id, 'only-their-tasks')
      }
    }

    if (user.showOnlyMyTasks) {
      const result = await Swal.fire({
        title: t('TEAMS:DeleteOnlyMyTasksFilterTitle'),
        text: t('TEAMS:DeleteOnlyMyTasksFilterContent'),
        icon: "info",
        showCancelButton: true,
        cancelButtonText: t('TEAMS:DeleteOnlyMyTasksFilterCancelButton'),
        confirmButtonText: t('TEAMS:DeleteOnlyMyTasksFilterConfirmButton'),
        width: "500px"
      })

      if (result.isConfirmed) {

        // dispatch(TeamsActions.toggleShowOnlyMyTasks(project._id, user_id, false))
        await setUserShowTasksMode(user_id, 'all')
      }
    }
  }

  const handleShowUserOptions = (user: any) => {

    setUser(user)
    setShowUserOptions(true)
  }

  const moveToTeam = async (toTeam: any) => {

    await moveUserToTeam(user, toTeam)

    history.replace(`/project/${project._id}/teams/${toTeam._id}`);

    setUser(undefined)
    setShowTeamSelector(false)
  }

  // const handleSetProfile = async (user: any, profileTag: string) => {

  //   if (profileTag === 'observer' || profileTag === 'collaborator') {

  //     await ProjectsService.removeAssistant(project._id, user._id)
  //   } else {

  //     await ProjectsService.addAssistant(project._id, user._id)
  //   }

  //   loadTeams()
  // }

  return (
    <Fragment>
      <div className='members-section'>
        <h5>{t('TEAMS:ActiveUsersTitle')} ({teamSelected.users.length})</h5>
        {teamSelected.users.length === 0 && (
          <div className='members-empty-list'>
            <div>
              Aún no hay usuarios activos
            </div>
          </div>
        )}
        {teamSelected.users.length > 0 && (
          <div className="members-content-list">
            {teamSelected.users.map((user: any, i: number) => (
              <div key={`${i}-${user._id}`} className="members-content-item">
                <div>
                  <UserAvatar user={user} />
                </div>
                <div className="member-texts">
                  <div className='member-name'>
                    {user.name} {user.surname}
                  </div>
                  <div className='member-email'>
                    {user.email}
                  </div>
                </div>
                <div className="member-profile">
                  <span className="profile-badge">
                    {user.isAssistant
                                  ? t('TEAMS:ListTableAssistantProfile')
                                  : user.profile}
                  </span>
                </div>
                <div className="member-actions">
                  <button
                    className="btn btn-light mr-1"
                    onClick={() => handleShowUserOptions(user)}
                  >
                    <i className="fas fa-fw fa-ellipsis-v"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <OptionsDialog
        show={showUserOptions}
        title={`${user?.name} ${user?.surname}`}
        closeButton
        onHide={() => {

          setShowUserOptions(false)
        }}
        options={options}
      />

      <TeamSelector
        show={showTeamSelector}
        teams={teams}
        teamSelected={teamSelected}
        onSelectTeam={(toTeam: any) => moveToTeam(toTeam)}
        onHide={() =>
          setShowTeamSelector(false)
        }
      />
    </Fragment>
  )
}

export default ActiveUsers
